import type { ParsedUrlQuery } from "querystring"

import { useRouter } from "next/router"
import { createContext, useContext } from "react"

import type { BasicObject, Nullable } from "@bounce/util"
import { notEmpty } from "@bounce/util"

export type ExperimentMap = BasicObject<string>

const FLAG_KEY_DELIMITER = ":"

export const parseFlagVariant = (segment: string) => {
  const [flagKey, variant] = segment.split(FLAG_KEY_DELIMITER)

  if (!flagKey || !variant) {
    return null
  }

  return { flagKey, variant }
}

export const stringifyFlagVariant = (flagKey: string, variant: string) => [flagKey, variant].join(FLAG_KEY_DELIMITER)

/**
 * Parses experiments from URL param into a Map.
 *
 * @param bucket - The catch-all route segments as a string, array, or undefined.
 * @returns A Map where keys are flag keys and values are variants.
 */
export const parseExperimentsToMap = (buckets: string[]): ExperimentMap => {
  return buckets.reduce<ExperimentMap>((map, segment) => {
    const parsed = parseFlagVariant(segment)
    if (!parsed) return map

    return { ...map, [parsed.flagKey]: parsed.variant }
  }, {})
}

type UseSSRExperimentHook = {
  isEnabled: boolean
  variant: Nullable<string>
}

export type UseSSRExperimentProps = {
  flagKey: string
  baselineValue?: string
  isSeoExperiment?: boolean
}

export const SSRExperimentsContext = createContext<ExperimentMap>({})

/**
 * Parses experiments from query parameters into an ExperimentMap.
 *
 * @param query - The query parameters containing bucket information
 * @returns A Map where keys are flag keys and values are variants.
 */
const parseExperimentsFromQuery = (query: ParsedUrlQuery | undefined): ExperimentMap => {
  const buckets = [query?.bucket].flat().filter(notEmpty)
  return parseExperimentsToMap(buckets)
}

export const SSRExperimentsProvider = ({ children }: { children: React.ReactNode }) => {
  const { query } = useRouter()
  const experiments = parseExperimentsFromQuery(query)

  return <SSRExperimentsContext.Provider value={experiments}>{children}</SSRExperimentsContext.Provider>
}

type GetExperimentResultProps = {
  baselineValue?: string
  flagKey: string
  experiments: ExperimentMap
  isSeoExperiment?: boolean
}

const getExperimentResult = ({
  baselineValue = "baseline",
  flagKey,
  experiments,
  isSeoExperiment = false,
}: GetExperimentResultProps) => {
  const variant = experiments[flagKey]

  if (isSeoExperiment) {
    // For SEO experiments, we invert the logic - treat baseline as experiment and vice versa
    return {
      isEnabled: !!variant && variant === baselineValue,
      variant,
    }
  }

  return {
    isEnabled: !!variant && variant !== baselineValue,
    variant,
  }
}

/**
 * Parses experiments and provides experiment status and variant.
 *
 * @param flagKey - The feature flag key to look for in the experiments.
 * @param baselineValue - The value to treat as the baseline/ control. Defaults to "baseline".
 * @param isSeoExperiment - If true, inverts which group gets the experiment for SEO purposes. Defaults to false.
 * @returns An object with `isEnabled` and `variant` properties.
 */
export const useSSRExperiment = ({
  baselineValue = "baseline",
  flagKey,
  isSeoExperiment = false,
}: UseSSRExperimentProps): UseSSRExperimentHook => {
  const experiments = useContext(SSRExperimentsContext)
  return getExperimentResult({ baselineValue, flagKey, experiments, isSeoExperiment })
}

export type GetSSRExperimentProps = {
  baselineValue?: string
  flagKey: string
  query: ParsedUrlQuery | undefined
  isSeoExperiment?: boolean
}

/**
 * Server-side version of useSSRExperiment that works without hooks.
 * Parses experiments from query parameters and provides experiment status and variant.
 *
 * @param flagKey - The feature flag key to look for in the experiments.
 * @param baselineValue - The value to treat as the baseline/control. Defaults to "baseline".
 * @param query - The Next.js query object containing experiment information.
 * @param isSeoExperiment - If true, inverts which group gets the experiment for SEO purposes. Defaults to false.
 * @returns An object with `isEnabled` and `variant` properties.
 *
 * @example
 * // In a getServerSideProps function:
 * export const getServerSideProps = async ({ query }) => {
 *   const experiment = getSSRExperiment({ flagKey: 'my-flag', query })
 *   return { props: { isExperimentEnabled: experiment.isEnabled } }
 * }
 */
export const getSSRExperiment = ({
  baselineValue = "baseline",
  flagKey,
  query,
  isSeoExperiment = false,
}: GetSSRExperimentProps) => {
  const experiments = parseExperimentsFromQuery(query)

  return getExperimentResult({ baselineValue, flagKey, experiments, isSeoExperiment })
}
